import React, { useEffect, useState } from "react";
import { api, fmtPKR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Banknote, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  suspended: "bg-red-500/15 text-red-300 border-red-500/30",
};

export default function AdminPayments() {
  const [shops, setShops] = useState([]);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const load = () => api.get("/admin/shops").then((r) => setShops(r.data));
  useEffect(() => {
    load();
    api.get("/admin/plans").then((r) => setPlans(r.data));
  }, []);

  const planMap = Object.fromEntries(plans.map((p) => [p.id, p]));

  const recordPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/shops/${selectedShop.id}/record-payment`, {
        amount: Number(amount),
        payment_date: date,
      });
      toast.success("Subscription payment recorded");
      setOpen(false);
      setAmount("");
      setSelectedShop(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const openFor = (shop) => {
    setSelectedShop(shop);
    setAmount(String(planMap[shop.plan_id]?.price_pkr || 0));
    setOpen(true);
  };

  const expiringSoon = (s) => {
    if (!s.trial_ends || s.status === "suspended") return false;
    const days = Math.floor((new Date(s.trial_ends).getTime() - Date.now()) / 86400000);
    return days <= 7 && days >= 0;
  };
  const expired = (s) => {
    if (!s.trial_ends || s.status === "suspended") return false;
    return new Date(s.trial_ends).getTime() < Date.now();
  };

  return (
    <div className="space-y-6" data-testid="admin-payments-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-white">Subscription Payments</h1>
        <p className="text-slate-400 mt-1">Track who paid, who's expiring, who's overdue.</p>
      </div>

      <div className="bg-[#151A23] border border-slate-800 rounded-md">
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <div className="text-sm text-slate-400">{shops.length} shops total</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Shop</th>
                <th className="text-left px-4 py-3 font-semibold">Plan</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Plan Price</th>
                <th className="text-left px-4 py-3 font-semibold">Trial / Expiry</th>
                <th className="text-right px-4 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {shops.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No shops yet.</td></tr>}
              {shops.map((s) => {
                const expSoon = expiringSoon(s);
                const exp = expired(s);
                return (
                  <tr key={s.id} className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`admin-pay-row-${s.id}`}>
                    <td className="px-4 py-3 font-medium text-white">{s.name}</td>
                    <td className="px-4 py-3 capitalize text-slate-300">{s.plan_id}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border ${STATUS_TONE[s.status] || STATUS_TONE.active}`}>{s.status}</span>
                    </td>
                    <td className="px-4 py-3 text-right tabular text-white">{fmtPKR(planMap[s.plan_id]?.price_pkr || 0)}</td>
                    <td className="px-4 py-3 text-xs tabular">
                      {s.trial_ends ? (
                        <span className={exp ? "text-red-400 font-semibold flex items-center gap-1" : expSoon ? "text-amber-400 font-semibold flex items-center gap-1" : "text-slate-400"}>
                          {(exp || expSoon) && <AlertTriangle className="w-3 h-3" />}
                          {s.trial_ends.slice(0, 10)}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" onClick={() => openFor(s)} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" data-testid={`admin-pay-record-${s.id}`}>
                        <Banknote className="w-4 h-4 mr-1" /> Record Payment
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[#151A23] text-slate-200 border border-slate-800" data-testid="admin-pay-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Record subscription payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={recordPayment} className="space-y-4">
            <div className="text-sm text-slate-400">
              Shop: <span className="text-white font-semibold">{selectedShop?.name}</span> ·
              Plan: <span className="text-white capitalize">{selectedShop?.plan_id}</span>
            </div>
            <div className="space-y-1.5">
              <Label>Amount (PKR)</Label>
              <Input type="number" required min={1} value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-slate-900 border-slate-700 text-slate-200" data-testid="admin-pay-amount" />
            </div>
            <div className="space-y-1.5">
              <Label>Payment date</Label>
              <Input type="date" required value={date} onChange={(e) => setDate(e.target.value)} className="bg-slate-900 border-slate-700 text-slate-200" data-testid="admin-pay-date" />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="admin-pay-submit">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
